import React, { useEffect, useState } from 'react';
import { Button, styled, Typography, IconButton, Box } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import ChatWindow from './ChatWindow';
import AILogo from '../../assets/Sophia-Profile.png';
import AppTheme from '../../shared-theme/AppTheme';
import CloseIcon from '@mui/icons-material/Close';

const ToggleButton = styled(Button)(({ theme }) => ({
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    width: '85px',
    height: '85px',
    borderRadius: '42.5px',
    border: 'none',
    cursor: 'pointer',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4), 0 4px 16px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: 0,
    minWidth: 'unset',
    ...theme.applyStyles('dark', {
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        boxShadow: '0 8px 32px rgba(26, 26, 46, 0.6), 0 4px 16px rgba(0, 0, 0, 0.3)',
    }),
    '&:hover': {
        width: '250px',
        borderRadius: '42.5px',
        background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
        boxShadow: '0 12px 40px rgba(124, 58, 237, 0.5), 0 6px 20px rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-2px) scale(1.02)',
        ...theme.applyStyles('dark', {
            background: 'linear-gradient(135deg, #2d1b69 0%, #1e3a8a 100%)',
            boxShadow: '0 12px 40px rgba(45, 27, 105, 0.7), 0 6px 20px rgba(0, 0, 0, 0.4)',
        }),
    },
    '&:active': {
        transform: 'translateY(0px) scale(0.98)',
        transition: 'all 0.1s ease',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
        transform: 'translateX(-100%)',
        transition: 'transform 0.6s ease',
    },
    '&:hover::before': {
        transform: 'translateX(100%)',
    },
    // Responsive design
    [theme.breakpoints.down('sm')]: {
        bottom: '20px',
        right: '20px',
        width: '70px',
        height: '70px',
        borderRadius: '35px',
        '&:hover': {
            width: '180px',
            borderRadius: '35px',
        },
    },
    [theme.breakpoints.down('xs')]: {
        bottom: '15px',
        right: '15px',
        width: '70px',
        height: '70px',
        borderRadius: '35px',
        '&:hover': {
            width: '180px',
            borderRadius: '35px',
        },
    },
    zIndex: 900
}));

const ButtonText = styled(Typography)(({ theme }) => ({
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '16px',
    letterSpacing: '0.1px',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    paddingLeft: '10px',
    paddingRight: '10px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    opacity: 0,
    transform: 'translateX(10px)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    ...theme.applyStyles('dark', {
        color: '#e2e8f0',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
    }),
}));

const LogoContainer = styled('div')(({ theme }) => ({
    width: '55px',
    height: '55px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '15px',
    flexShrink: 0,
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(5px)',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s ease',
    '& img': {
        width: '45px',
        height: '45px',
        borderRadius: '50%',
        objectFit: 'cover',
    },
    [theme.breakpoints.down('sm')]: {
        width: '50px',
        height: '50px',
        marginLeft: '12px',
        '& img': {
            width: '40px',
            height: '40px',
        },
    },
    [theme.breakpoints.down('xs')]: {
        width: '45px',
        height: '45px',
        marginLeft: '12px',
        '& img': {
            width: '35px',
            height: '35px',
        },
    },
}));

const ButtonContent = styled('div')(({ isHovered }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    '& .button-text': {
        opacity: isHovered ? 1 : 0,
        transform: isHovered ? 'translateX(0)' : 'translateX(10px)',
    },
}));
const NotificationDotContainer = styled('div')(({ theme }) => ({
    position: 'absolute',
    top: '-4px',
    right: '0px',
    width: '16px',
    height: '16px',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#ffffff',
}));
const NotificationDot = styled('div')(({ theme }) => ({
    position: 'absolute',
    top: '-4px',
    right: '-2px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #ff4757 0%, #ff3838 100%)',
    border: '2px solid #ffffff',
    boxShadow: '0 2px 8px rgba(255, 71, 87, 0.6), 0 0 20px rgba(255, 71, 87, 0.3)',
    animation: 'pulse 2s infinite',
    zIndex: 9001,

    '@keyframes pulse': {
        '0%': {
            transform: 'scale(1)',
            boxShadow: '0 2px 8px rgba(255, 71, 87, 0.6), 0 0 20px rgba(255, 71, 87, 0.3)',
        },
        '50%': {
            transform: 'scale(1.1)',
            boxShadow: '0 2px 12px rgba(255, 71, 87, 0.8), 0 0 25px rgba(255, 71, 87, 0.5)',
        },
        '100%': {
            transform: 'scale(1)',
            boxShadow: '0 2px 8px rgba(255, 71, 87, 0.6), 0 0 20px rgba(255, 71, 87, 0.3)',
        },
    },
    [theme.breakpoints.down('sm')]: {
        width: '14px',
        height: '14px',
        top: '6px',
        right: '6px',
    },
    [theme.breakpoints.down('xs')]: {
        width: '12px',
        height: '12px',
        top: '5px',
        right: '5px',
    },
}));

const TeaserWrapper = styled('div')(({ theme }) => ({
    position: 'fixed',
    right: '30px',
    bottom: '125px',
    width: '320px',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#ffffff',
    boxShadow: '0 12px 40px rgba(0,0,0,0.15), 0 6px 20px rgba(102, 126, 234, 0.25)',
    border: '1px solid rgba(102, 126, 234, 0.25)',
    zIndex: 901,
    animation: 'floatTeaser 4s ease-in-out infinite',
    '@keyframes floatTeaser': {
        '0%': { transform: 'translateY(0px)' },
        '50%': { transform: 'translateY(-6px)' },
        '100%': { transform: 'translateY(0px)' }
    },
    ...theme.applyStyles('dark', {
        background: 'rgba(11,14,20,0.95)',
        border: '1px solid rgba(102, 126, 234, 0.35)'
    }),
    [theme.breakpoints.down('sm')]: {
        right: '16px',
        bottom: '110px',
        width: '280px'
    }
}));

const TeaserHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    background: '#0b57d0',
    color: '#ffffff',
    fontWeight: 700,
    letterSpacing: '0.2px'
}));

const TeaserBody = styled('div')(({ theme }) => ({
    display: 'flex',
    gap: '10px',
    padding: '14px 16px',
    background: '#ffffff',
    ...theme.applyStyles('dark', {
        background: 'rgba(17, 24, 39, 0.95)',
        color: '#e2e8f0'
    })
}));

const testMessages = [
    {
        messageBody: 'Welcome to the chat! Responses are generated by AI.',
        type: 'System'
    },
    {
        messageBody: `Hello! 👋

I'm **Sophia**, your friendly representative here to help you with all things related to higher education experiences at **Miami Dade College (MDC)**. If you have any questions or need assistance, feel free to ask! 😊

#### How Can I Assist You Today?

-   **Financial Aid Information**
-   **Academic Programs**
-   **Student Life**
-   **Campus Resources**

Let me know how I can help!`,
        type: 'AI'
    }
];

const ChatToggle = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState(testMessages);
    const [isHovered, setIsHovered] = useState(false);
    const [chatId, setChatId] = useState(null);
    const [hasBeenClicked, setHasBeenClicked] = useState(false);
    const [showTeaser, setShowTeaser] = useState(true);
    
    const sendMessage = (message) => {
        setMessages([...messages, message]);
    };

    const handleButtonClick = () => {
        if (!hasBeenClicked) {
            setHasBeenClicked(true);
        }
        setIsOpen(!isOpen);
        setShowTeaser(false);
    };

    useEffect(() => {
        if (!hasBeenClicked && showTeaser) {
            const timer = setTimeout(() => setShowTeaser(false), 20000);
            return () => clearTimeout(timer);
        }
    }, [hasBeenClicked, showTeaser]);
  return (<>
    {(!hasBeenClicked && showTeaser) && (
      <TeaserWrapper role="dialog" aria-label="Chat teaser">
        <TeaserHeader>
          <span>May I help you?</span>
          <IconButton
            size="small"
            onClick={() => setShowTeaser(false)}
            sx={{ color: '#ffffff' }}
            aria-label="Close teaser"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </TeaserHeader>
        <TeaserBody onClick={handleButtonClick} style={{ cursor: 'pointer' }}>
          <img src={AILogo} alt="AI Assistant" style={{ width: '36px', height: '36px', borderRadius: '50%', marginTop: '6px' }} />
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
            Hi there! I’m a Sophia, your friendly AI assistant here to answer your questions.
          </Typography>
        </TeaserBody>
      </TeaserWrapper>
    )}
    <ToggleButton
      onClick={handleButtonClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setTimeout(() => setIsHovered(false), 200)}
      sx={{
        display: {
          xs: isOpen ? 'none' : 'inline-flex',
          sm: 'inline-flex',
        },
        position: 'fixed',
      }}
    >
   
      <ButtonContent isHovered={isHovered}>
        <LogoContainer>
          <img src={AILogo} alt="AI Assistant" />
          {!hasBeenClicked ? <NotificationDot /> : <NotificationDotContainer >
            💬
          </NotificationDotContainer>}
        </LogoContainer>
        <ButtonText className="button-text" variant="body1">
          Chat with Sophia
        </ButtonText>
      </ButtonContent>
    </ToggleButton>
    <ChatWindow 
      messages={messages} 
      setMessages={setMessages} 
      onClose={() => setIsOpen(false)} 
      isOpen={isOpen} 
      chatId={chatId} 
      setChatId={setChatId} 
    />
  </>);
};

export default ChatToggle;
